'use client';

/**
 * SETTINGS — engineering calibration, same soma.settings()/updateSetting() as classic /settings.
 * Field spec mirrors classic SETTING_GROUPS (8 groups). Works in Electron (IPC) and browser (gateway).
 */
import { useEffect, useState } from 'react';
import { soma } from '@/lib/api';
import { Glass, EmptyState, mono, display } from '@/future/primitives';

type Field = { path: string; label: string; type: 'boolean' | 'number' | 'select'; options?: string[]; min?: number; max?: number };
type Group = { title: string; fields: Field[] };

const GROUPS: Group[] = [
  { title: 'AUTO-BREATHE', fields: [
    { path: 'breathe.auto', label: 'Auto-breathe', type: 'boolean' },
    { path: 'breathe.triggerAt', label: 'Trigger at %', type: 'number', min: 20, max: 90 },
    { path: 'breathe.rotateAt', label: 'Rotate at %', type: 'number', min: 40, max: 95 },
    { path: 'breathe.graceSeconds', label: 'Grace seconds', type: 'number', min: 10, max: 120 },
  ]},
  { title: 'CONTEXT WARNINGS', fields: [
    { path: 'context.notifyAt', label: 'Notify at %', type: 'number', min: 20, max: 90 },
    { path: 'context.warnAt', label: 'Warn at %', type: 'number', min: 30, max: 95 },
    { path: 'context.urgentAt', label: 'Urgent at %', type: 'number', min: 50, max: 95 },
    { path: 'context.autoExhaleAt', label: 'Auto-exhale at %', type: 'number', min: 70, max: 99 },
  ]},
  { title: 'HEAT SYSTEM', fields: [
    { path: 'protocols.warmThreshold', label: 'Warm threshold', type: 'number', min: 1, max: 10 },
    { path: 'protocols.hotThreshold', label: 'Hot threshold', type: 'number', min: 3, max: 15 },
    { path: 'protocols.maxHeat', label: 'Max heat', type: 'number', min: 5, max: 30 },
    { path: 'protocols.decayRate', label: 'Decay rate', type: 'number', min: 0, max: 5 },
    { path: 'heat.autoDetect', label: 'Auto-detect', type: 'boolean' },
    { path: 'heat.pinBump', label: 'Pin bump', type: 'number', min: 1, max: 15 },
  ]},
  { title: 'MUSCLES', fields: [
    { path: 'muscles.tokenBudget', label: 'Token budget', type: 'number', min: 500, max: 10000 },
    { path: 'muscles.maxFull', label: 'Max full', type: 'number', min: 1, max: 10 },
    { path: 'muscles.maxDigest', label: 'Max digest', type: 'number', min: 1, max: 20 },
    { path: 'muscles.fullThreshold', label: 'Full threshold', type: 'number', min: 1, max: 10 },
  ]},
  { title: 'KEEPALIVE', fields: [
    { path: 'keepalive.maxPings', label: 'Max pings', type: 'number', min: 0, max: 5 },
    { path: 'keepalive.autoExhale', label: 'Auto-exhale', type: 'boolean' },
  ]},
  { title: 'GUARD', fields: [
    { path: 'guard.coreFiles', label: 'Core files', type: 'select', options: ['allow', 'warn', 'block'] },
    { path: 'guard.bashCommands', label: 'Dangerous commands', type: 'select', options: ['allow', 'warn', 'block'] },
  ]},
  { title: 'PRELOAD', fields: [
    { path: 'preload.staleAfterHours', label: 'Stale after hours', type: 'number', min: 1, max: 168 },
  ]},
  { title: 'CHECKPOINTS', fields: [
    { path: 'checkpoints.enabled', label: 'Enabled', type: 'boolean' },
    { path: 'checkpoints.intervalMinutes', label: 'Interval (min)', type: 'number', min: 1, max: 60 },
  ]},
];

const get = (obj: any, path: string) => path.split('.').reduce((o, k) => o?.[k], obj);

export function SettingsSurface() {
  const [settings, setSettings] = useState<any>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    soma.settings()
      .then((d: any) => { if (d && typeof d === 'object' && !d.error) { setSettings(d); setState('ready'); } else setState('unavailable'); })
      .catch(() => setState('unavailable'));
  }, []);

  const update = async (path: string, value: any) => {
    setSaving(path);
    try {
      const r = await soma.updateSetting(path, value);
      if (r?.ok) setSettings((prev: any) => {
        const next = JSON.parse(JSON.stringify(prev));
        const parts = path.split('.');
        let o = next;
        for (let i = 0; i < parts.length - 1; i++) { o[parts[i]] = o[parts[i]] || {}; o = o[parts[i]]; }
        o[parts[parts.length - 1]] = value;
        return next;
      });
    } finally { setSaving(null); }
  };

  if (state === 'loading') return <div className="pt-10 text-center" style={{ ...mono(18, '.3em'), color: 'var(--uif-t5)' }}>READING CALIBRATION…</div>;
  if (state === 'unavailable') return <EmptyState what="CALIBRATION OFFLINE" action="Settings need the gateway (Electron) — open classic /settings there." />;

  return (
    <div className="mx-auto max-w-5xl grid grid-cols-2 gap-4">
      {GROUPS.map(g => (
        <Glass key={g.title} className="p-5">
          <div style={{ ...mono(16, '.3em'), color: 'var(--uif-t5)' }}>{g.title}</div>
          <div className="mt-3 flex flex-col gap-2.5">
            {g.fields.map(f => {
              const v = get(settings, f.path);
              return (
                <div key={f.path} className="flex items-center gap-3">
                  <div className="w-44" style={{ ...mono(17, '.06em'), color: 'var(--uif-t3)' }}>{f.label}</div>
                  <div className="ml-auto flex items-center gap-2">
                    {f.type === 'boolean' && (
                      <button onClick={() => update(f.path, !v)}
                        className="relative w-9 h-5 rounded-full transition-colors"
                        style={{ background: v ? 'var(--uif-cy-interactive)' : 'rgba(255,255,255,.1)', opacity: saving === f.path ? .5 : 1 }}>
                        <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform"
                          style={{ left: 2, transform: v ? 'translateX(16px)' : 'none' }} />
                      </button>
                    )}
                    {f.type === 'number' && (
                      <input type="number" defaultValue={v ?? ''} min={f.min} max={f.max}
                        onBlur={e => { const n = Number(e.target.value); if (!Number.isNaN(n) && n !== v) update(f.path, n); }}
                        className="w-20 text-right rounded px-2 py-1"
                        style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.09)', color: 'var(--uif-t1)', fontSize: 22, outline: 'none' }} />
                    )}
                    {f.type === 'select' && (
                      <select value={v ?? ''} onChange={e => update(f.path, e.target.value)}
                        className="rounded px-2 py-1"
                        style={{ background: 'rgba(20,28,36,.9)', border: '1px solid rgba(255,255,255,.09)', color: 'var(--uif-t1)', fontSize: 22, outline: 'none' }}>
                        {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Glass>
      ))}
    </div>
  );
}
